/**
 * TableOfContents Component - Fixed
 *
 * IMPROVEMENTS:
 * - No longer relies on brittle class name selectors
 * - Uses closest scrollable parent automatically
 * - More robust scrolling logic
 */

import React, { useState, useEffect } from "react";
import styles from "../styles/TableOfContents.module.css";

interface TOCItem {
    id: string;
    label: string;
}

interface TableOfContentsProps {
    items: TOCItem[];
}

export default function TableOfContents({ items }: TableOfContentsProps) {
    const [activeSection, setActiveSection] = useState<string>("");

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            {
                rootMargin: "-20% 0px -70% 0px",
            }
        );

        // Observe all sections
        items.forEach((item) => {
            const element = document.getElementById(item.id);
            if (element) {
                observer.observe(element);
            }
        });

        return () => observer.disconnect();
    }, [items]);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);

        if (element) {
            // Find closest scrollable parent (more robust than hard-coded class names)
            let scrollContainer: Element | Window | null = null;
            let parent = element.parentElement;

            while (parent) {
                const overflowY = window.getComputedStyle(parent).overflowY;
                if (overflowY === 'auto' || overflowY === 'scroll') {
                    scrollContainer = parent;
                    break;
                }
                parent = parent.parentElement;
            }

            // If we found a scrollable container, scroll within it
            if (scrollContainer && scrollContainer instanceof Element) {
                const headerOffset = 100;
                const containerRect = scrollContainer.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const scrollTop = scrollContainer.scrollTop;
                const relativeTop = elementRect.top - containerRect.top + scrollTop - headerOffset;

                scrollContainer.scrollTo({
                    top: relativeTop,
                    behavior: "smooth",
                });
            } else {
                // Fallback to window scroll
                const headerOffset = 100;
                const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                const offsetPosition = elementPosition - headerOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                });
            }
        }
    };

    return (
        <nav className={styles.toc}>
            <div className={styles.tocTitle}>On this page</div>
            <ul className={styles.tocList}>
                {items.map((item) => (
                    <li key={item.id} className={styles.tocItem}>
                        <button
                            type="button"
                            className={`${styles.tocLink} ${
                                activeSection === item.id ? styles.active : ""
                            }`}
                            onClick={() => scrollToSection(item.id)}
                        >
                            {item.label}
                        </button>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
